import { useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import matterService from '@api/v1/matterService'
import timeEntryService from '@api/v1/timeEntryService'
import { requireArray } from '@api/v1/readShape'
import LoadingSpinner from '@components/common/LoadingSpinner'
import CaseLawChat from '@components/legal/CaseLawChat'
import useDocumentTitle from '@hooks/useDocumentTitle'
import useReadState from '@hooks/useReadState'

const getGreeting = () => {
  const hour = dayjs().hour()
  if (hour < 12) return 'Good morning'
  if (hour < 17) return 'Good afternoon'
  return 'Good evening'
}

const StatCard = ({ label, value, subtext, color = 'bg-cb-accent' }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
    <div className="flex items-center justify-between mb-3">
      <p className="text-sm font-medium text-gray-500">{label}</p>
      <div className={`w-2.5 h-2.5 rounded-full ${color}`} />
    </div>
    <p className="text-2xl font-semibold text-gray-900 font-display">{value}</p>
    {subtext && <p className="text-xs text-gray-500 mt-1">{subtext}</p>}
  </div>
)

// Parse "Description - Number - Client" composite name
function parseName(name) {
  if (!name) return { desc: '', client: '' }
  const parts = name.split(' - ')
  if (parts.length >= 3) return { desc: parts.slice(0, -2).join(' - '), client: parts[parts.length - 1].trim() }
  return { desc: name, client: '' }
}

const Dashboard = () => {
  useDocumentTitle('Dashboard')
  const { user } = useAuth0()
  const navigate = useNavigate()
  const [showCaseLaw, setShowCaseLaw] = useState(false)
  const firstName = user?.name?.split(' ')[0] || user?.nickname || 'there'

  /*
   * CC-53132: three outcomes per read, not two — see `@api/v1/readState`.
   *
   * Every number and every empty line on this page used to be computed from
   * `recentMatters?.length` / `(recentEntries || [])`, i.e. from the DATA, with
   * `isLoading` as the only gate. `isLoading` is `isPending && isFetching`, so it
   * is FALSE for a query that is pending but NOT in flight — above all a PAUSED
   * one (react-query's default `networkMode: 'online'` while `onlineManager`
   * believes the browser is offline). `queryFn` is never called, nothing throws,
   * nothing is logged, and `data` stays `undefined`.
   *
   * On this page that produced the whole set at once: "Billable Hours Today 0.0
   * / 0 entries today", "This Week 0.0 hrs / 0 entries", "No recent matters
   * found", "No active timer", "No entries today" — a complete, confident,
   * indefinite account of a day nobody had asked the server about. This is the
   * first screen a timekeeper sees in the morning; "0.0 billable today" and
   * especially "No active timer" are the two statements most likely to change
   * what they do next, and both of them were free-running guesses.
   *
   * The hook rather than the bare `readState()` because it carries the permanent
   * blocked-read diagnostic (CC-49899: a fail-soft path you cannot observe is
   * indistinguishable from a fix).
   *
   * `requireArray` in the queryFns for the two list reads, not `|| []` at the
   * render sites: both services return a bare `resp.data`, so a 200 whose body
   * is not the list hands this page `undefined` on a read that DID complete.
   * That is the readShape.js family and the standing rule is not to write
   * another `|| []`. A genuine `[]` is left completely alone. `getActiveTimer`
   * is deliberately NOT wrapped — it returns an object or a legitimate null.
   */
  const mattersQuery = useQuery({
    queryKey: ['recentMatters'],
    queryFn: async () => requireArray(await matterService.getRecentMatters(), {
      noun: 'recent matters list', emptyNoun: 'set of matters', entryNoun: 'matter',
    }),
  })
  const { refetch: refetchMatters } = mattersQuery
  const mattersState = useReadState('dashboard-recent-matters', mattersQuery)

  const timerQuery = useQuery({
    queryKey: ['activeTimer'],
    queryFn: () => timeEntryService.getActiveTimer(),
    refetchInterval: 30000,
  })
  const { refetch: refetchTimer } = timerQuery
  const timerState = useReadState('dashboard-active-timer', timerQuery)

  // Get recent time entries for "Billable Hours Today" and "This Week" stats
  const today = dayjs().format('YYYY-MM-DD')
  const entriesQuery = useQuery({
    queryKey: ['recentEntries'],
    queryFn: async () => requireArray(
      await timeEntryService.getRecentEntries({ beforeDate: today, limit: 200 }),
      { noun: 'recent billing entries', emptyNoun: 'set of entries', entryNoun: 'entry' }
    ),
  })
  const { refetch: refetchEntries } = entriesQuery
  const entriesState = useReadState('dashboard-recent-entries', entriesQuery)

  /*
   * The single gate for everything derived below. Nothing on this page reads
   * `*Query.data` directly, because react-query keeps the PREVIOUS `data`
   * alongside a fresh error — so a failed refresh would otherwise still publish
   * a count, sourced from a read that is currently known to be broken. Gating on
   * the STATE rather than on the value covers that as well as the never-asked
   * case, and `requireArray` above makes the converse hold: 'ready' implies an
   * array, so every site that survives the state checks may index freely.
   *
   * `undefined` is the honest value here and it is allowed to propagate: the
   * derived figures below stay `undefined` too, and the render sites choose
   * between a real number and a visible refusal on exactly that distinction.
   */
  const knownMatters = mattersState === 'ready' ? mattersQuery.data : undefined
  const knownEntries = entriesState === 'ready' ? entriesQuery.data : undefined
  const activeTimer = timerState === 'ready' ? timerQuery.data : undefined

  // Compute stats from entries
  const todayEntries = knownEntries?.filter((e) => e.date?.split('T')[0] === today)
  const billableToday = todayEntries?.filter((e) => !e.isNonBillable).reduce((s, e) => s + e.hours, 0)
  const weekStart = dayjs().startOf('week').format('YYYY-MM-DD')
  const weekEntries = knownEntries?.filter((e) => {
    const d = e.date?.split('T')[0]
    return d >= weekStart && d <= today
  })
  const billableWeek = weekEntries?.filter((e) => !e.isNonBillable).reduce((s, e) => s + e.hours, 0)

  return (
    <div className="max-w-7xl mx-auto">
      {/* Greeting */}
      <div className="mb-8">
        <h1 className="text-2xl font-display text-gray-900">
          {getGreeting()}, {firstName}
        </h1>
        <p className="text-sm text-gray-500 mt-1">{dayjs().format('dddd, MMMM D, YYYY')}</p>
      </div>

      {/* Stat Cards */}
      {/* CC-53132: four figures, none of which may be stated on an unresolved
          read. `--` is this page's existing vocabulary for "we don't know" (the
          two right-hand cards already used it), so the two hour figures join it
          rather than reporting `0.0` — a zero beside an unfinished read is a
          claim, not an observation, and "0.0 billable today" is a claim a
          timekeeper acts on. The count SUBTEXTS are dropped entirely in that
          case: there is no honest "N entries" to write, and `--- entries today`
          would just be a second guess. Which read is unresolved, and how to
          retry it, is said once in the panel that owns it below — the two hour
          cards and Total Entries all come from the one entries read that the
          "Today's Entries" panel reports on. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard
          label="Billable Hours Today"
          value={todayEntries ? billableToday.toFixed(1) : '--'}
          subtext={todayEntries ? `${todayEntries.length} entries today` : undefined}
          color="bg-cb-teal"
        />
        <StatCard
          label="This Week"
          value={weekEntries ? `${billableWeek.toFixed(1)} hrs` : '--'}
          subtext={weekEntries ? `${weekEntries.length} entries` : undefined}
          color="bg-cb-accent"
        />
        <StatCard
          label="Active Matters"
          value={knownMatters ? knownMatters.length : '--'}
          subtext="Recent matters"
          color="bg-cb-orange"
        />
        <StatCard
          label="Total Entries"
          value={knownEntries ? knownEntries.length : '--'}
          subtext="Recent billing entries"
          color="bg-cb-green"
        />
      </div>

      {/* Quick Actions */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => setShowCaseLaw(true)}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-cb-navy bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-cb-navy/30 transition-colors shadow-sm"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
          </svg>
          Case Law Research
        </button>
      </div>

      {/* Case Law Research Panel */}
      {showCaseLaw && (
        <div className="fixed inset-y-0 right-0 w-[480px] max-w-full z-50 shadow-2xl">
          <div className="absolute inset-0">
            <CaseLawChat onClose={() => setShowCaseLaw(false)} />
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Matters */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <h2 className="text-base font-semibold text-gray-900">Recent Matters</h2>
            <button
              onClick={() => navigate('/matters')}
              className="text-xs text-cb-accent-text hover:underline"
            >
              View all
            </button>
          </div>
          <div className="divide-y divide-gray-50">
            {mattersState === 'blocked' ? (
              /* The state that used to render as "No recent matters found".
                 react-query PAUSED the fetch, so `queryFn` was never called: no
                 request, no error, no spinner, nothing in any log on either
                 side. A spinner forever would be nearly as bad, so say what
                 actually happened and offer the retry. */
              <div role="alert" className="m-5 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                <p className="font-medium">Can&rsquo;t reach the server, so we haven&rsquo;t loaded your matters.</p>
                <p className="mt-1 text-amber-800">
                  This is not an empty list — the matter list was never read. Your connection
                  looks offline; check it and try again.
                </p>
                <button
                  type="button"
                  onClick={() => refetchMatters()}
                  className="mt-3 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-900 hover:bg-amber-100"
                >
                  Try again
                </button>
              </div>
            ) : mattersState === 'failed' ? (
              /* Failed. This read had NO error surface at all before — the
                 query's `error` was never destructured, so a thrown read
                 rendered byte-identically to a firm with no recent matters. */
              <div role="alert" className="m-5 rounded-lg border border-red-100 bg-red-50 p-4 text-sm text-red-700">
                <p className="font-medium">We couldn&rsquo;t load your recent matters: {mattersQuery.error?.message}</p>
                <p className="mt-1">
                  This is not an empty list — nothing was read, so no matter is missing.
                </p>
                <button
                  type="button"
                  onClick={() => refetchMatters()}
                  className="mt-3 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100"
                >
                  Try again
                </button>
              </div>
            ) : mattersState === 'loading' || mattersState === 'idle' ? (
              /* `idle` rides with `loading`. This query carries no `enabled:`
                 option, so it cannot reach that state today — but every branch
                 here is gated on a NAMED state, and an unhandled fifth one
                 renders NOTHING, which is a blank panel for whoever adds an
                 `enabled` gate later. Safe to fold in while that stays true;
                 if an `enabled` condition does appear, split it out FIRST,
                 because `idle` then means "not asked yet" and this spinner
                 would never resolve. */
              <div className="p-8 flex justify-center">
                <LoadingSpinner message="Loading matters..." />
              </div>
            ) : knownMatters.length > 0 ? (
              knownMatters.slice(0, 8).map((matter, i) => {
                const parsed = parseName(matter.name)
                return (
                  <div
                    key={matter.matterId || i}
                    onClick={() => navigate(`/matters/${matter.id || matter.matterId || matter.objectId}`)}
                    className="px-5 py-3 hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    <div className="flex items-center justify-between">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {parsed.desc || matter.name || 'Unnamed Matter'}
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {matter.number || ''}{parsed.client ? ` — ${parsed.client}` : ''}
                        </p>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${matter.isActive !== false ? 'text-cb-teal-dark bg-cb-teal/10' : 'text-gray-500 bg-gray-100'}`}>
                        {matter.isActive !== false ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                )
              })
            ) : (
              <div className="p-8 text-center text-sm text-gray-500">
                No recent matters found
              </div>
            )}
          </div>
        </div>

        {/* Right Column: Timer + Recent Time */}
        <div className="space-y-6">
          {/* Active Timer */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100">
            <div className="px-5 py-4 border-b border-gray-100">
              <h2 className="text-base font-semibold text-gray-900">Active Timer</h2>
            </div>
            <div className="p-5">
              {/* CC-53132: of everything on this page, "No active timer" is the
                  sentence with the worst consequences when it is a guess — a
                  timekeeper who believes nothing is running starts a second
                  timer, or stops recording, and the hours are gone. It had no
                  loading state and no error state at all: `activeTimer` was read
                  straight off the query, so pending, paused, failed and
                  genuinely-no-timer were one branch. Note the poll: with
                  `refetchInterval: 30000` a BACKGROUND refetch keeps
                  `status: 'success'`, so a running timer does not flicker into
                  the loading branch every 30s; only an actual failure or pause
                  moves it, and when that happens saying so is the point. */}
              {timerState === 'blocked' ? (
                <div role="alert" className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                  <p className="font-medium">Can&rsquo;t reach the server, so we don&rsquo;t know if a timer is running.</p>
                  <p className="mt-1 text-amber-800">
                    This is not &ldquo;no timer&rdquo; — nothing was read. Check your connection before
                    starting another one.
                  </p>
                  <button
                    type="button"
                    onClick={() => refetchTimer()}
                    className="mt-3 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-900 hover:bg-amber-100"
                  >
                    Try again
                  </button>
                </div>
              ) : timerState === 'failed' ? (
                <div role="alert" className="rounded-lg border border-red-100 bg-red-50 p-4 text-sm text-red-700">
                  <p className="font-medium">We couldn&rsquo;t check your timer: {timerQuery.error?.message}</p>
                  <p className="mt-1">
                    This is not &ldquo;no timer&rdquo; — nothing was read. Check before starting another one.
                  </p>
                  <button
                    type="button"
                    onClick={() => refetchTimer()}
                    className="mt-3 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100"
                  >
                    Try again
                  </button>
                </div>
              ) : timerState === 'loading' || timerState === 'idle' ? (
                /* `idle` folded into loading: no `enabled:` option on this query,
                   so it is unreachable — but an unhandled state renders nothing
                   at all, and a blank card is the one outcome worse than a wrong
                   one here. Plain text rather than `LoadingSpinner` so the two
                   other panels' spinners stay individually addressable. */
                <p className="text-sm text-gray-500 text-center py-4">Checking for a running timer&hellip;</p>
              ) : activeTimer ? (
                <div>
                  <p className="text-sm font-medium text-gray-900 mb-1">
                    {activeTimer.matterName || 'Timer Running'}
                  </p>
                  <p className="text-xs text-gray-500 mb-3">{activeTimer.description || ''}</p>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-cb-green rounded-full animate-pulse" />
                    <span className="text-sm font-medium text-cb-green-dark">Running</span>
                  </div>
                </div>
              ) : (
                <div className="text-center py-4">
                  <div className="w-12 h-12 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-3">
                    <svg className="w-6 h-6 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <p className="text-sm text-gray-500">No active timer</p>
                </div>
              )}
            </div>
          </div>

          {/* Today's Entries */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-base font-semibold text-gray-900">Today&apos;s Entries</h2>
              <button
                onClick={() => navigate('/time-entries')}
                className="text-xs text-cb-accent-text hover:underline"
              >
                View all
              </button>
            </div>
            <div className="divide-y divide-gray-50">
              {entriesState === 'blocked' ? (
                /* Paused: no request was sent. This panel and the three entry
                   figures at the top of the page share the one read, so this is
                   where that read explains itself and offers the retry. */
                <div role="alert" className="m-5 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                  <p className="font-medium">Can&rsquo;t reach the server, so we haven&rsquo;t loaded today&rsquo;s entries.</p>
                  <p className="mt-1 text-amber-800">
                    This is not a day with no time on it — your entries were never read, which is
                    also why the hours above show &ldquo;--&rdquo;. Your connection looks offline; check it
                    and try again.
                  </p>
                  <button
                    type="button"
                    onClick={() => refetchEntries()}
                    className="mt-3 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-900 hover:bg-amber-100"
                  >
                    Try again
                  </button>
                </div>
              ) : entriesState === 'failed' ? (
                <div role="alert" className="m-5 rounded-lg border border-red-100 bg-red-50 p-4 text-sm text-red-700">
                  <p className="font-medium">We couldn&rsquo;t load today&rsquo;s entries: {entriesQuery.error?.message}</p>
                  <p className="mt-1">
                    This is not a day with no time on it — nothing was read, which is also why the
                    hours above show &ldquo;--&rdquo;.
                  </p>
                  <button
                    type="button"
                    onClick={() => refetchEntries()}
                    className="mt-3 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100"
                  >
                    Try again
                  </button>
                </div>
              ) : entriesState === 'loading' || entriesState === 'idle' ? (
                /* `idle` folded into loading — no `enabled:` option on this query,
                   same reasoning as the matters panel. Plain text rather than
                   `LoadingSpinner` so the matters spinner stays the only one. */
                <p className="p-5 text-center text-sm text-gray-500">Loading today&rsquo;s entries&hellip;</p>
              ) : todayEntries.length > 0 ? (
                todayEntries.slice(0, 5).map((entry, i) => (
                  <div key={entry.id || i} className="px-5 py-2.5">
                    <div className="flex items-center justify-between">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-gray-900 truncate">{entry.description || entry.matterName || entry.name || '--'}</p>
                        <p className="text-xs text-gray-500">{entry.timekeeper || ''}</p>
                      </div>
                      <p className="text-sm font-medium text-gray-900 ml-3">{entry.hours.toFixed(1)}h</p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-5 text-center text-sm text-gray-500">
                  No entries today
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Dashboard
